import React from 'react'
import _ from 'lodash'
import OnSuccessDelegate from '../../types/OnSuccessDelegate'
import OnErrorDelegate from '../../types/OnErrorDelegate'
import { Http } from 'lib'
import { Promise } from 'bluebird'
import { setWordBanks, setIsLoading, setIsSubmitting, setIsLoadingCategories, Top5Action } from './store/Actions'
import {
  IWordBank,
  WordBankStatus,
  WordBankCategory,
  IGetWordBanksPayload,
  IGetWordBanksResponse,
  ICreateOwnWordBankPayload,
  ICreateOwnWordBankResponse,
  IResponseUpdateRequestPayload
} from 'types'
import { Top5Context } from './Provider'
import apiUrlsAddresses from '../../config/constants/api-urls-addresses'

export default class Top5Service {
  static async getWordBanks({
    category,
    userId
  }: IGetWordBanksPayload): Promise<[IGetWordBanksResponse | null, number, string | null]> {
    return Http.get<IGetWordBanksResponse>(apiUrlsAddresses.wordBanks.get({ category, userId }))
  }

  static async addOwnWordBank(
    payload: ICreateOwnWordBankPayload
  ): Promise<[ICreateOwnWordBankResponse | null, number, string | null]> {
    return Http.post<ICreateOwnWordBankPayload, ICreateOwnWordBankResponse>(
      apiUrlsAddresses.wordBanks.create(),
      payload
    )
  }

  static isValidateNewSelfTag(state: Top5Context['state'], payload: ICreateOwnWordBankPayload) {
    const wordbanks = state.wordBanks[payload.wordbank.category]
    const isSelfTag = payload.wordbank.category === WordBankCategory.TalentTags
    const userWordbanks = state.userWordbanks[payload.wordbank.category]

    const existentTag = wordbanks.some(tag => {
      const isSameValue = tag.value.toLowerCase().trim() === payload.wordbank.value.toLowerCase().trim()

      if (isSelfTag) {
        return tag.subType === payload.wordbank.subType && isSameValue
      }
      return isSameValue
    })

    const duplicatedTag = userWordbanks.some(tag => {
      const isSameValue = tag.value.toLowerCase().trim() === payload.wordbank.value.toLowerCase().trim()

      if (isSelfTag) {
        return tag.subType === payload.wordbank.subType && isSameValue
      }

      return isSameValue
    })

    const msgDuplicated = duplicatedTag ? 'This tag already was added.' : null
    const msgExistent = existentTag ? 'This tag already exists.' : null

    return msgDuplicated || msgExistent
  }

  static onAddOwnClick(
    dispatch: React.Dispatch<Top5Action>,
    state: Top5Context['state'],
    updateResponse: OnSuccessDelegate<IResponseUpdateRequestPayload>,
    onError: OnErrorDelegate
  ) {
    return async (payload: ICreateOwnWordBankPayload) => {
      dispatch(setIsSubmitting(payload.wordbank.category, true))

      const error = this.isValidateNewSelfTag(state, payload)
      if (error) {
        onError(error)
      } else {
        const [response, , requestError] = await Top5Service.addOwnWordBank(payload)

        if (Boolean(requestError)) {
          onError(requestError as string)
        } else {
          const category = response.data.category
          const wordbanks = [...state.wordBanks[category], response.data]
          dispatch(setWordBanks(category, wordbanks))
          await Top5Service.onTagClick(state, dispatch, updateResponse, onError)(response.data)
        }
      }

      dispatch(setIsSubmitting(payload.wordbank.category, false))
    }
  }

  static onMount(dispatch: React.Dispatch<Top5Action>, onError: OnErrorDelegate) {
    return async (userId: string) => {
      const categories = [WordBankCategory.CompanyDescriptor, WordBankCategory.Xfactor, WordBankCategory.TalentTags]
      dispatch(setIsLoadingCategories(true))

      await Promise.map(categories, async category => {
        dispatch(setIsLoading(category, true))

        const [response, , requestError] = await Top5Service.getWordBanks({ category, userId })

        if (Boolean(requestError)) {
          onError(requestError as string)
        } else {
          dispatch(setWordBanks(category, response.data))
        }

        dispatch(setIsLoading(category, false))
      })

      dispatch(setIsLoadingCategories(false))
    }
  }

  static onTagClick(
    state: Top5Context['state'],
    dispatch: React.Dispatch<Top5Action>,
    updateResponse: OnSuccessDelegate<IResponseUpdateRequestPayload>,
    onError: OnErrorDelegate
  ) {
    return async (wordbank: IWordBank) => {
      const questionKey = Top5Service.wordBankCategoryQuestionKeyMap[wordbank.category]
      const currentTags = state.userWordbanks[wordbank.category]
      const maxItems = state.rules[wordbank.category].maxItems
      const removed = _.remove(currentTags, (tag: IWordBank) => {
        return tag._id === wordbank._id
      })

      if (!removed.length) {
        if (currentTags.length + 1 > maxItems) {
          onError(`You can select up to ${maxItems}`)
          return
        }
        currentTags.push(wordbank)
      } else if (removed[0].status === WordBankStatus.Deleted) {
        const category = removed[0].category
        const wordbanks = state.wordBanks[category]
        _.remove(wordbanks, (tag: IWordBank) => {
          return tag._id === wordbank._id
        })
        dispatch(setWordBanks(category, wordbanks))
      }

      updateResponse({ questionKey, response: currentTags })
    }
  }

  static wordBankCategoryQuestionKeyMap = {
    [WordBankCategory.TalentTags]: 'selfTag',
    [WordBankCategory.Xfactor]: 'teamMembersSay',
    [WordBankCategory.CompanyDescriptor]: 'thriveEnv'
  }

  static canAddNewTag(state: Top5Context['state'], onError: OnErrorDelegate) {
    return (category: WordBankCategory) => {
      const selectedWordBanks = state.userWordbanks[category]
      const maxTags = state.rules[category].maxItems
      const shouldRender = selectedWordBanks.length < maxTags

      if (!shouldRender) {
        onError(`You can select up to ${maxTags}`)
      }
      return shouldRender
    }
  }
}
